import { Box, Flex, Image, Input, Spinner, Text } from "@chakra-ui/react";
import { SendSvg } from "@root/assets/icons";
import { PauseButton } from "@root/components/common/PauseButton";
import { StyledTextareaContainer } from "@root/components/dashboard/style";
import { useMainChatContext } from "@root/context/kps-chat-context";
import { useTranslation } from "react-i18next";
import { useRef, useEffect, useState } from "react";
import { useRiskAssessmentContext } from "@root/context/risk-assessment-context";
import { useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  updateFileIndex,
  updateFileIndexType,
} from "@root/store/slices/entities";
import { isDoazGptChat } from "@root/utils/chatHelper";
import { isPdfUrlValidThunk } from "@root/store/thunks";
import { validateUrl } from "@root/utils/helpers";
import { WEB_PROJECTS, PDF } from "@root/webConstants";
import {
  FILE_STATUS,
  PAGINATION_DEFAULTS,
  QUERY_TYPE,
  ROUTE_NAMES,
  FILE_TYPE,
  AI_MODEL_OPTIONS,
  CHAT_STATUS,
} from "@root/constants";
import {
  createDocument,
  processDocument,
} from "@root/store/thunks/documentsActions";
import MultiDocSend from "@root/assets/icons/multidoc-send";
// Avoid importing hook here to prevent separate state instance; delegate to parent
// import useProjectsDashboard from "@root/hooks/useProjectsDashboard";
import FileDropdown from "../checklist/Filesdropdown";
import { AssessmentIcon } from "@root/assets/icons/AssessmentIcon";
import PromptIcon from "@root/assets/icons/Prompt.svg";
import { ChevronDownIcon } from "@chakra-ui/icons";

function getFileType(fileName: string): "pdf" | "docx" | "image" | "other" {
  if (fileName?.toLowerCase().endsWith(".pdf")) return "pdf";
  if (fileName?.toLowerCase().endsWith(".docx")) return "docx";
  if (/(\.png|\.jpg|\.jpeg|\.gif|\.bmp)$/i.test(fileName)) return "image";
  return "other";
}

export const MultiAskAITextArea = ({
  onChangehandler,
  input,
  onSend,
  fileIndex,
  noOfDocs,
  loading,
  name = "",
  pid = WEB_PROJECTS.KSCE_GPT.id,
  stopPolling,
  currentStatus,
}: any) => {
  const [allDocuments, setAllDocuments] = useState<UploadedDocument[]>([]);
  const {
    isMessaging,
    urlType,
    setFlagValidateUrl,
    fetchPdfPreview,
    setPdfLinkLoading,
    deepSearch,
    attachedFile,
    setAttachedFile,
    selectedProject,
    docChatMode,
    currentPathname,
    documentsData,
    fetchAllDocs,
  } = useMainChatContext();
  // const { onSubmitHandler } = useProjectsDashboard();

  const completedDocs = documentsData?.rows?.filter(
    (doc: any) => doc?.status === 8
  );

  // Multi-AI model selection state (shared via hook)
  const { selectedMultiAiModel, setSelectedMultiAiModel } =
    useRiskAssessmentContext();
  const [isAIModelDropdownOpen, setIsAIModelDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<"top" | "bottom">(
    "bottom"
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isSendDisabled = input?.trim?.()?.length === 0;
  const isModelSelected = Boolean(selectedMultiAiModel?.id);
  const isButtonDisabled =
    isSendDisabled || Boolean(loading) || !isModelSelected;

  const doazInputRef: any = useRef(null);
  const inputRef: any = useRef(null);
  const dispatch = useDispatch();
  const { t } = useTranslation("common");
  const location = useLocation();
  const isDoazGPTChat = isDoazGptChat(pid);
  const { pathname } = useLocation();
  const isMultiAIChatbot = pathname === ROUTE_NAMES.MULTI_AI_CHATBOT;

  // Attachment state
  const [attachLoading, setAttachLoading] = useState(false);
  const [attachStatus, setAttachStatus] = useState<
    | null
    | typeof FILE_STATUS.COMPLETED
    | typeof FILE_STATUS.FAILED
    | typeof FILE_STATUS.PROCESSING
  >(null);

  const handleAIModelSelect = (option: { id: number; labelKey: string }) => {
    setSelectedMultiAiModel(option);
    setIsAIModelDropdownOpen(false);
  };

  const handleAIModelDropdownToggle = () => {
    if (!isAIModelDropdownOpen && dropdownRef.current) {
      // Calculate position when opening dropdown
      const rect = dropdownRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownHeight = 200; // Approximate dropdown height

      // If there's not enough space below, open upward
      if (rect.bottom + dropdownHeight > viewportHeight) {
        setDropdownPosition("top");
      } else {
        setDropdownPosition("bottom");
      }
    }
    setIsAIModelDropdownOpen(!isAIModelDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsAIModelDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Fetch documents when component mounts
  useEffect(() => {
    if (pathname.includes(ROUTE_NAMES.MULTI_AI_CHATBOT)) {
      const payload = {
        page: 1,
        limit: PAGINATION_DEFAULTS.MAX,
        type: FILE_TYPE.SIMPLE_FILE,
      };
      fetchAllDocs(payload);
    }
  }, [pathname]);

  // Polling for process status using processDocument response
  useEffect(() => {
    let polling: any;

    // Find first file with status "processing"
    const fileToPoll = attachedFile?.find(
      (file: any) => file.status === FILE_STATUS.PROCESSING
    );

    if (fileToPoll) {
      polling = setInterval(async () => {
        try {
          const res: any = await dispatch(
            processDocument({
              id: fileToPoll.docId,
              onSuccess: (resp: any) => resp,
              onError: () => setAttachStatus(FILE_STATUS.FAILED),
            })
          );

          const status = res?.payload?.status;

          if (status === 3) {
            fileToPoll.status = FILE_STATUS.COMPLETED;
            setAttachStatus(FILE_STATUS.COMPLETED);
            clearInterval(polling);
          }
          if (status === 4) {
            fileToPoll.status = FILE_STATUS.FAILED;
            setAttachStatus(FILE_STATUS.FAILED);
            clearInterval(polling);
          }
        } catch {
          setAttachStatus(FILE_STATUS.FAILED);
          clearInterval(polling);
        }
      }, 2000);
    }
    return () => clearInterval(polling);
  }, [attachedFile, dispatch]);

  useEffect(() => {
    if (inputRef?.current) {
      inputRef.current.style.height = "42px";
      inputRef.current.style.height = `${inputRef?.current?.scrollHeight}px`;
    }
  }, [inputRef?.current?.scrollHeight]);

  const onChangeHandler = (e: any) => {
    if (inputRef?.current) {
      inputRef.current.style.height = "42px";
      inputRef.current.style.height = `${inputRef?.current?.scrollHeight}px`;
    }
    setFlagValidateUrl?.(true);
    onChangehandler?.(e);
  };

  const getPlaceholderText = () => {
    return t("multiAI.input.placeholder");
  };

  const onKeyPressHandler = async (e: any) => {
    switch (e.keyCode) {
      case 40:
        if (fileIndex < noOfDocs) {
          dispatch(updateFileIndex(fileIndex + 1));
          dispatch(updateFileIndexType("inc"));
        }
        break;
      case 38:
        if (fileIndex > 1) {
          dispatch(updateFileIndex(fileIndex - 1));
          dispatch(updateFileIndexType("dec"));
        }
        break;
      case 13:
        if (!e?.shiftKey) {
          e.preventDefault();
          let validate = false;
          const url = inputRef?.current?.value;
          if (urlType?.value === PDF) {
            try {
              setPdfLinkLoading?.(true);
              const res: any = await dispatch(
                isPdfUrlValidThunk({ payload: { url } })
              );
              validate = res?.payload?.data;
              fetchPdfPreview?.({ url });
            } catch (err) {
              validate = false;
            } finally {
              setPdfLinkLoading?.(false);
            }
          } else {
            validate = Boolean(validateUrl?.(urlType?.value, url));
          }
          setFlagValidateUrl?.(validate);
          if (validate) onSubmit?.();
        }
        break;
    }
  };

  // Attachment: open file select
  const onAttachmentClick = () => {
    if (doazInputRef?.current) doazInputRef.current.value = "";
    doazInputRef?.current?.click?.();
  };

  const onAttachmentFileSelect = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = Array.from(e?.target?.files || []);
    if (!files?.length) return;

    const allowedExt = [
      "pdf",
      "doc",
      "docx",
      "xls",
      "xlsx",
      "ppt",
      "pptx",
      "txt",
      "png",
      "jpg",
      "jpeg",
      "gif",
      "bmp",
    ];

    // Filter valid files
    const validFiles = files?.filter((file) => {
      const ext = file?.name?.split?.(".")?.pop?.()?.toLowerCase?.();
      return (
        ext && allowedExt?.includes?.(ext) && file?.size <= 50 * 1024 * 1024
      );
    });

    if (!validFiles?.length) {
      setAttachStatus?.(FILE_STATUS.FAILED);
      return;
    }

    setAttachLoading?.(true);
    const newFiles = [...(attachedFile || [])];

    try {
      for (const file of validFiles) {
        try {
          const formData = new FormData();
          formData?.append?.("file", file);

          const uploadRes: any = await dispatch(
            createDocument({
              formData,
              onSuccess: (res: any) => res,
              onError: () => {
                newFiles?.push?.(
                  Object?.assign?.(file, { status: FILE_STATUS.FAILED })
                );
              },
            })
          );

          // Start processing the document immediately
          try {
            const processRes: any = await dispatch(
              processDocument({
                id: uploadRes?.payload?.id,
                onSuccess: (resp: any) => resp,
                onError: () => {
                  newFiles?.push?.(
                    Object?.assign?.(file, { status: FILE_STATUS.FAILED })
                  );
                },
              })
            );

            const doc = processRes?.payload;
            if (doc?.id) {
              const fileWithDocId = Object?.assign?.(file, {
                docId: doc?.id,
                path: doc?.signedUrl,
                status: FILE_STATUS.PROCESSING,
              });
              newFiles?.push?.(fileWithDocId);

              const status = doc?.status;
              if (status === 3) {
                setAttachedFile?.((prevFiles: any) =>
                  prevFiles?.map?.((f: any) =>
                    f?.docId === doc?.id
                      ? { ...f, status: FILE_STATUS.COMPLETED }
                      : f
                  )
                );
              } else if (status === 4) {
                setAttachedFile?.((prevFiles: any) =>
                  prevFiles?.map?.((f: any) =>
                    f?.docId === doc?.id
                      ? { ...f, status: FILE_STATUS.FAILED }
                      : f
                  )
                );
              }
            } else {
              newFiles?.push?.(
                Object?.assign?.(file, { status: FILE_STATUS.FAILED })
              );
            }
          } catch (processError) {
            console?.error?.("Processing error:", processError);
            newFiles?.push?.(
              Object?.assign?.(file, { status: FILE_STATUS.FAILED })
            );
          }
        } catch (error) {
          newFiles?.push?.(
            Object?.assign?.(file, { status: FILE_STATUS.FAILED })
          );
        }
      }
      setAttachedFile?.(newFiles);
      setAttachStatus?.(FILE_STATUS.PROCESSING);
    } catch (error) {
      console?.error?.("Upload error:", error);
      setAttachStatus?.(FILE_STATUS.FAILED);
    } finally {
      setAttachLoading?.(false);
    }
  };

  const removeAttachment = (identifier: string) => {
    setAttachedFile?.((prev: any) =>
      prev?.filter?.((file: any) => file?.id !== identifier)
    );
  };

  const onSubmit = () => {
    if (isButtonDisabled) {
      return;
    }
    const input = inputRef?.current?.value;
    onSend?.(input, selectedMultiAiModel?.id);
    return;
  };

  const chatButton = () => {
    if (!loading)
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor={isButtonDisabled ? "not-allowed" : "pointer"}
          onClick={isButtonDisabled ? () => {} : onSubmit}
          width="40px"
          height="40px"
          borderRadius="50%"
          bg="#005EB8"
          opacity={isButtonDisabled ? "0.5" : "1"}
          _hover={{
            bg: "blue.600",
          }}
          transition="all 0.2s"
        >
          <SendSvg />
        </Box>
      );
    else if (loading && currentStatus === CHAT_STATUS?.STREAMING)
      return <PauseButton onClick={() => stopPolling?.()} />;
    else
      return (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="40px"
          height="40px"
          borderRadius="50%"
          bg="#005EB8"
          opacity={isButtonDisabled ? "0.5" : "1"}
          _hover={{
            bg: "blue.600",
          }}
          transition="all 0.2s"
        >
          <Spinner color="white" />
        </Box>
      );
  };

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      position={"relative"}
      className="DashBoardTextarea"
      bg="white"
      border="1px solid #CBD5E1"
      borderRadius="16px"
      cursor="text"
      // Clicking anywhere in the box (except its buttons) focuses the text field
      onClick={(e: any) => {
        if (!(e.target as HTMLElement)?.closest?.("button")) inputRef?.current?.focus();
      }}
      boxShadow="0 1px 2px rgba(15, 23, 42, 0.06), 0 6px 16px rgba(15, 23, 42, 0.05)"
      transition="border-color 0.15s, box-shadow 0.15s"
      _focusWithin={{
        borderColor: "#005EB8",
        boxShadow: "0 0 0 3px rgba(0, 94, 184, 0.12)",
      }}
    >
      <Box w={"100%"} h={"100%"} borderRadius={"16px"} bg={"white"}>
        <Flex
          position={"relative"}
          width={"100%"}
          alignItems="center"
          gap="12px"
          px="16px"
          py="8px"
        >
          {/* Text Input */}
          <StyledTextareaContainer
            ref={inputRef}
            onChange={onChangeHandler}
            border={0}
            outline={0}
            value={input}
            onKeyDown={
              loading || !isModelSelected
                ? () => {}
                : isMessaging
                ? () => {}
                : onKeyPressHandler
            }
            name={name}
            placeholder={getPlaceholderText()}
            flex="1"
            maxH={"100%"}
            minH="24px"
            overflowY={"auto"}
            fontSize="16px"
            lineHeight="1.5"
            _placeholder={{
              color: "#9CA3AF",
              fontSize: "16px",
            }}
          />
        </Flex>
        <Flex
          px={"16px"}
          justifyContent={"space-between"}
          alignItems={"center"}
          w={"100%"}
          pt="10px"
          pb="16px"
        >
          <Flex gap={"12px"} alignItems={"center"}>
            {/* Attachment Icon */}
            {/* <Flex
              width={"40px"}
              height={"40px"}
              borderRadius={"50%"}
              border="1px solid #E5E7EB"
              display="flex"
              alignItems={"center"}
              justifyContent={"center"}
              cursor="pointer"
              onClick={onAttachmentClick}
            >
              <PaperClipIcon />
            </Flex> */}

            {/* Multi-AI Model Selection Dropdown */}
            <Box position="relative" ref={dropdownRef}>
              <Box
                p="1px"
                borderRadius="60px"
                bgGradient="linear(180deg, #005EB8 0%, #B15906 100%)"
                cursor="pointer"
                onClick={handleAIModelDropdownToggle}
                _hover={{
                  bgGradient: "linear(180deg, #005EB8 0%, #B15906 100%)",
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap="8px"
                  px="12px"
                  py="8px"
                  h="40px"
                  borderRadius="60px"
                  bg="white"
                  _hover={{ bg: "#F9FAFB" }}
                >
                  <Box display="flex" alignItems="center" gap="6px">
                    <Image src={PromptIcon} />
                    <Text fontSize="14px" color="#010610" fontWeight="500">
                      {selectedMultiAiModel?.labelKey
                        ? t(selectedMultiAiModel.labelKey)
                        : t("multiAI.models.selectModels")}
                    </Text>
                  </Box>
                  <Box width="1px" height="16px" bg="#E5E7EB" />
                  <Box
                    width="12px"
                    height="12px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <ChevronDownIcon />
                  </Box>
                </Box>
              </Box>

              {/* Dropdown Menu */}
              {isAIModelDropdownOpen && (
                <Box
                  position="absolute"
                  {...(dropdownPosition === "top"
                    ? { bottom: "100%", mb: "4px" }
                    : { top: "100%", mt: "4px" })}
                  left="0"
                  right="0"
                  bg="white"
                  borderRadius="8px"
                  w="280px"
                  border="1px solid #E5E7EB"
                  boxShadow="0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -1px rgba(0, 0, 0, 0.06)"
                  zIndex={1000}
                >
                  {AI_MODEL_OPTIONS?.map((option, index) => (
                    <Box
                      key={option.id}
                      px="12px"
                      py="8px"
                      cursor="pointer"
                      bg={
                        selectedMultiAiModel?.id === option?.id
                          ? "#F3F4F6"
                          : "white"
                      }
                      _hover={{
                        bg:
                          selectedMultiAiModel?.id === option?.id
                            ? "#F3F4F6"
                            : "#F9FAFB",
                      }}
                      onClick={() => handleAIModelSelect?.(option)}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      <Text fontSize="14px" color="#374151">
                        {option?.labelKey
                          ? t(option.labelKey)
                          : (option as any)?.label}
                      </Text>
                      {selectedMultiAiModel?.id === option?.id && (
                        <Box
                          width="16px"
                          height="16px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Box
                            width="12px"
                            height="12px"
                            display="flex"
                            alignItems="center"
                            justifyContent="center"
                          >
                            <Text
                              fontSize="12px"
                              color="#374151"
                              fontWeight="bold"
                            >
                              ✓
                            </Text>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>

            {/* Selected AI Model Chips */}
            {/* {selectedAIModels.map((model) => (
              <Box
                key={model}
                display="flex"
                alignItems="center"
                gap="4px"
                px="12px"
                py="8px"
                borderRadius="60px"
                bg="#E0F2FE"
                border="1px solid #0EA5E9"
              >
                <Text fontSize="14px" color="#0C4A6E" fontWeight="500">
                  {model}
                </Text>
                <Box
                  width="16px"
                  height="16px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  cursor="pointer"
                  borderRadius="50%"
                  _hover={{
                    bg: "#BAE6FD",
                  }}
                  onClick={() => handleAIModelRemove?.(model)}
                >
                  <Text fontSize="12px" color="#0C4A6E" fontWeight="bold">
                    ×
                  </Text>
                </Box>
              </Box>
            ))} */}

            {/* Hidden input for file */}
            <Input
              ref={doazInputRef}
              type="file"
              accept=".pdf, .doc, .docx, .xls, .xlsx, .ppt, .pptx, .txt, .png, .jpg, .jpeg, .gif, .bmp"
              onChange={onAttachmentFileSelect}
              display={"none"}
              multiple={true}
            />
          </Flex>

          {/* Send Button - shown when not loading */}
          {chatButton()}
        </Flex>
      </Box>
    </Flex>
  );
};
